import React from 'react';
import { getDifficultyStyle, difficultyRanges } from '@/shared/utils/constants';
import ComingSoonCell from './ComingSoonCell';

interface CellRendererParams {
  value: number | null | undefined;
  context?: {
    language?: 'fa' | 'en';
    isDark?: boolean;
  };
}

const DifficultyCellRenderer = (params: CellRendererParams) => {
  const value = params.value;
  const language = params.context?.language || 'fa';
  // Check dark mode from both context and DOM class
  const isDark = params.context?.isDark || document.documentElement.classList.contains('dark');
  
  if (value === null || value === undefined) {
    return <ComingSoonCell language={language} isDark={isDark} />;
  }

  const style = getDifficultyStyle(value);
  
  if (!style) {
    return <ComingSoonCell language={language} isDark={isDark} />;
  }

  // Generate tooltip HTML content with dark mode support
  const titleText = language === 'fa' ? 'سختی کلمه کلیدی' : 'Keyword Difficulty';
  // In dark mode: white background, dark text
  // In light mode: dark background, white text
  const bgColor = isDark ? '#ffffff' : 'rgb(31, 41, 55)';
  const textColor = isDark ? 'rgb(31, 41, 55)' : 'white';
  const borderColor = isDark ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)';
  const labelColor = isDark ? 'rgb(55, 85, 99)' : 'rgb(209, 213, 219)';
  const dashColor = isDark ? 'rgb(107, 114, 128)' : 'rgb(107, 114, 128)';
  
  const rangesHTML = difficultyRanges.map(range => {
    const label = language === 'en' ? range.enLabel : range.label;
    return `
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; font-size: 14px;">
        <span style="color: ${labelColor}; flex: 1;">
          ${label}
        </span>
        <div style="display: flex; align-items: center; gap: 4px;">
          <span style="background-color: ${range.bgColor}; color: ${range.color}; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 11px; min-width: 32px; text-align: center;">
            ${range.max}
          </span>
          <span style="color: ${dashColor}; margin: 0 4px;">-</span>
          <span style="background-color: ${range.bgColor}; color: ${range.color}; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 11px; min-width: 32px; text-align: center;">
            ${range.min}
          </span>
        </div>
      </div>
    `;
  }).join('');

  const tooltipHTML = `
    <div style="background-color: ${bgColor}; border-radius: 8px; padding: 16px; min-width: 280px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);">
      <div style="color: ${textColor}; font-weight: 600; font-size: 14px; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid ${borderColor};">
        ${titleText}
      </div>
      ${rangesHTML}
    </div>
  `;

  // Update tooltip HTML on mouse enter to ensure it's always current
  const handleMouseEnter = (e: React.MouseEvent<HTMLSpanElement>) => {
    const tooltipElement = document.querySelector('[data-tooltip-id="difficulty-tooltip-global"]');
    if (tooltipElement) {
      const currentIsDark = document.documentElement.classList.contains('dark');
      const currentBgColor = currentIsDark ? '#ffffff' : 'rgb(31, 41, 55)';
      const currentTextColor = currentIsDark ? 'rgb(31, 41, 55)' : 'white';
      const currentBorderColor = currentIsDark ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)';
      const currentLabelColor = currentIsDark ? 'rgb(55, 85, 99)' : 'rgb(209, 213, 219)';
      
      const updatedRangesHTML = difficultyRanges.map(range => {
        const label = language === 'en' ? range.enLabel : range.label;
        return `
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; font-size: 14px;">
            <span style="color: ${currentLabelColor}; flex: 1;">
              ${label}
            </span>
            <div style="display: flex; align-items: center; gap: 4px;">
              <span style="background-color: ${range.bgColor}; color: ${range.color}; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 11px; min-width: 32px; text-align: center;">
                ${range.max}
              </span>
              <span style="color: rgb(107, 114, 128); margin: 0 4px;">-</span>
              <span style="background-color: ${range.bgColor}; color: ${range.color}; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 11px; min-width: 32px; text-align: center;">
                ${range.min}
              </span>
            </div>
          </div>
        `;
      }).join('');

      const updatedTooltipHTML = `
        <div style="background-color: ${currentBgColor}; border-radius: 8px; padding: 16px; min-width: 280px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);">
          <div style="color: ${currentTextColor}; font-weight: 600; font-size: 14px; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid ${currentBorderColor};">
            ${titleText}
          </div>
          ${updatedRangesHTML}
        </div>
      `;
      
      e.currentTarget.setAttribute('data-tooltip-html', updatedTooltipHTML);
    }
  };

  return (
    <span
      data-tooltip-id="difficulty-tooltip-global"
      data-tooltip-place="top"
      data-tooltip-html={tooltipHTML}
      onMouseEnter={handleMouseEnter}
      style={{
        backgroundColor: style.bgColor,
        color: style.color,
        padding: '2px 14px',
        borderRadius: '4px',
        fontWeight: '600',
        fontSize: '12px',
        display: 'inline-block',
        minWidth: '0px',
        textAlign: 'center',
        lineHeight: '2.4',
        cursor: 'pointer'
      }}
    >
      {value}
    </span>
  );
};

export default DifficultyCellRenderer;

